<template>
  <div class="step-bank-overlay">
    <div class="overlay-backdrop" @click="$emit('close')" />
    <div class="overlay-sheet">
      <button type="button" class="sheet-close" aria-label="close" @click="$emit('close')">
        <img :src="icons.close" alt="" />
      </button>

      <h1 class="sheet-heading" dir="auto">
        לפני שעוברים ל{{ bankName }}, כמה דברים שכדאי לשים לב אליהם בזמן החיבור:
      </h1>

      <ul class="tip-list">
        <li class="tip-card tip-card--tall">
          <div class="tip-body">
            <h3 class="tip-title" dir="auto">יש לך חשבון עם שותפ.ה?</h3>
            <p class="tip-text" dir="auto">
              צריך לוודא שזיהוי המשתמש מול הבנק מתאים לתעודת הזהות שהכנסת לפני רגע.
            </p>
          </div>
          <div class="tip-badge">
            <img class="tip-badge-star" :src="stars.green" alt="" />
            <img class="tip-badge-icon tip-badge-icon--light" :src="icons.users" alt="" />
          </div>
        </li>

        <li class="tip-card">
          <div class="tip-body">
            <p class="tip-text" dir="auto">
              <span>כדאי לשים לב שהדפדפן </span>
              <strong>במצב רגיל</strong>
              <span> (לא גלישה פרטית / incognito)</span>
            </p>
          </div>
          <div class="tip-badge">
            <img class="tip-badge-star" :src="stars.dark" alt="" />
            <img class="tip-badge-icon tip-badge-icon--light" :src="icons.globe" alt="" />
          </div>
        </li>

        <li class="tip-card">
          <div class="tip-body">
            <p class="tip-text" dir="auto">
              <span>אם יש לך </span>
              <strong>חוסם פרסומות פעיל</strong>
              <span> - צריך לנטרל אותו לפני חיבור החשבון (ולאחר מכן אפשר להחזיר)</span>
            </p>
          </div>
          <div class="tip-badge">
            <img class="tip-badge-star" :src="stars.lime" alt="" />
            <img class="tip-badge-icon tip-badge-icon--dark" :src="icons.shield" alt="" />
          </div>
        </li>
      </ul>

      <div class="cta-area">
        <CtaButton
          :text="'קחו אותי ל' + bankName"
          @click="$emit('continue')"
        />
      </div>
    </div>
  </div>
</template>

<script>
import CtaButton from './CtaButton.vue';

/* eslint-disable global-require */
const icons = {
  close: require('../assets/lucide-x.svg'),
  users: require('../assets/lucide-users-round.svg'),
  globe: require('../assets/lucide-globe.svg'),
  shield: require('../assets/lucide-shield-check.svg'),
};
const stars = {
  green: require('../assets/star13-green.svg'),
  dark: require('../assets/star12-light.svg'),
  lime: require('../assets/star14-lime.svg'),
};
/* eslint-enable global-require */

export default {
  name: 'StepBankOverlay',
  components: { CtaButton },
  props: {
    bankName: {
      type: String,
      required: true,
    },
  },
  data() {
    return { icons, stars };
  },
};
</script>

<style lang="scss" scoped>
// The overlay is scoped to .flow-phone-frame (the simulated phone UI area)
// — not the full desktop viewport.
.step-bank-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  pointer-events: none; // children opt in below
}

.overlay-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #202125;
  opacity: 0.89;
  pointer-events: auto;
}

.overlay-sheet {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  // Cover the bottom ~84% of the phone frame, leaving a dark strip at the top.
  // Because the overlay lives inside .flow-phone-frame it inherits that frame's
  // height, so % here is the right unit.
  height: 84%;
  background: #f9f7ed;
  border-radius: 36px 36px 0 0;
  padding: 32px 20px 24px;
  display: flex;
  flex-direction: column;
  pointer-events: auto;
  overflow-y: auto;

  @media (max-width: 500px) {
    height: 86%;
    padding: 24px 16px 20px;
  }
}

.sheet-close {
  position: absolute;
  top: 18px;
  right: auto;
  left: 24px;
  width: 22px;
  height: 22px;
  padding: 0;
  background: none;
  border: 0;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    display: block;
  }
}

.sheet-heading {
  font-family: 'SimplerProAlte', sans-serif;
  font-size: 24px;
  line-height: 32px;
  font-weight: 700;
  // No font-style: italic — SimplerProAlte has no italic glyph, so iOS Chrome
  // (and some Android browsers) render a synthetic slant that looks broken.
  font-style: normal;
  font-synthesis: none;
  color: #205441;
  text-align: center;
  margin: 32px auto 34px;
  max-width: 326px;

  @media (max-width: 500px) {
    font-size: 22px;
    line-height: 30px;
    margin: 32px auto 28px;
  }
}

.tip-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.tip-card {
  position: relative;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 17px;
  min-height: 73px;
  padding: 14px 72px 14px 22px;
  display: flex;
  align-items: center;

  &--tall {
    min-height: 95px;
    padding-top: 18px;
    padding-bottom: 18px;
  }
}

.tip-body {
  flex: 1;
  text-align: right;
}

.tip-title {
  font-family: 'SimplerPro', sans-serif;
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
  color: #205441;
  margin: 0 0 4px;
}

.tip-text {
  font-family: 'SimplerPro', sans-serif;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: #205441;
  margin: 0;

  strong {
    font-weight: 700;
  }
}

.tip-badge {
  position: absolute;
  top: 50%;
  right: 18px;
  transform: translateY(-50%);
  width: 42px;
  height: 42px;
}

.tip-badge-star {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
}

.tip-badge-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 22px;
  height: 22px;
  display: block;

  &--light {
    filter: brightness(0) invert(1);
  }
}

.cta-area {
  margin-top: auto;
  padding-top: 24px;
  padding-bottom: 8px;
}

// Slide-up panel animation. The parent transition applies
// .overlay-slide-enter-active / .overlay-slide-enter / etc. classes to the
// root .step-bank-overlay element.
.overlay-slide-enter-active,
.overlay-slide-leave-active {
  .overlay-backdrop {
    transition: opacity 0.35s ease;
  }
  .overlay-sheet {
    transition: transform 0.45s cubic-bezier(0.16, 1, 0.3, 1);
  }
}

.overlay-slide-enter,
.overlay-slide-leave-to {
  .overlay-backdrop {
    opacity: 0;
  }
  .overlay-sheet {
    transform: translateY(100%);
  }
}
</style>
